import { ReactNode } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faQuestion,
} from "@fortawesome/sharp-regular-svg-icons";
import { Accordion, Modal } from "@mantine/core";
import { Condition } from "shared/src/data/medication-condition-pairs";

import styles from "./faq-modal.module.scss";

type FaqModalProps = {
  isOpen: boolean;
  onClose: () => void;
  condition: string | null | undefined;
  initiallyOpenedQuestion?: string;
};

const Title = () => (
  <div className={styles.headingIcon}>
    <FontAwesomeIcon icon={faQuestion} />
  </div>
);

type FrequentlyAskedQuestion = {
  question: string;
  answer: string | ReactNode;
};

const howAreDocumentsAttachedToThePatientsAppeal = {
  question: "How are documents attached to the patient's appeal?",
  answer: (
    <ul>
      <li>
        {
          "All supporting documents are attached to the appendix at the end of the patient's appeal as supporting materials."
        }
      </li>
    </ul>
  ),
};

const areDocumentsRequired = {
  question: "Are documents required?",
  answer: (
    <>
      <p>
        {"Documents are not required, but strengthen the patient’s appeal."}
      </p>
      <br />
      <p>{"We highly recommend:"}</p>
      <ul>
        <div>
          <li>
            <div>
              <strong>{"Letter of medical necessity"}</strong>{" "}
              {"for the patient’s treatment plan"}
            </div>
          </li>
          <li>
            <div>
              <strong>{"Medical Records"}</strong>{" "}
              {"that confirm the patient’s diagnoses"}
            </div>
          </li>
          <li>
            <div>
              <strong>{"Lab Results"}</strong>{" "}
              {"that support evidence of condition(s)"}
            </div>
          </li>
        </div>
      </ul>
    </>
  ),
};

const whatDocsCanIAdd = {
  question: "What documents can I add?",
  answer: (
    <>
      <p>{"You can add:"}</p>
      <ul>
        <li>{"Denial Letter"}</li>
        <li>{"Letter of medical necessity / doctor’s statement"}</li>
        <li>{"Medical records"}</li>
        <li>{"Lab results"}</li>
        <li>
          {
            "Other applicable documents that support the patient’s treatment needs"
          }
        </li>
      </ul>
    </>
  ),
};
const whoWillSeeUploadedDocuments = {
  question: "Who will see uploaded documents?",
  answer: (
    <ul>
      <li>
        {"The denial letter will "}
        <strong>{"not"}</strong>
        {
          " attached to the patient's formal appeal - they are for reference only."
        }
      </li>
      <li>
        <div>
          {
            "All other documents will be added as appendix items to the patient's appeal and shared with:"
          }
          <ul>
            <li>
              {
                "The appeal department and CEO of the patient's insurance company"
              }
            </li>
            <li>{"Any additional recipients the patient's select."}</li>
          </ul>
        </div>
      </li>
      <li>
        <strong>
          {"All documents will be reviewed by the patient before submission."}
        </strong>
      </li>
    </ul>
  ),
};
const needMoreHelp = {
  question: "Need more help?",
  answer: (
    <p>
      {"Contact us at:"}{" "}
      <a href="mailto:providers@getclaimable.com">
        {"providers@getclaimable.com"}
      </a>
    </p>
  ),
};
const faqData: FrequentlyAskedQuestion[] = [
  areDocumentsRequired,
  whatDocsCanIAdd,
  whoWillSeeUploadedDocuments,
  howAreDocumentsAttachedToThePatientsAppeal,
  needMoreHelp,
];
const obesityFaqData: FrequentlyAskedQuestion[] = [
  areDocumentsRequired,
  {
    question: "What documents are recommended for GLP-1 appeals?",
    answer: (
      <>
        <p>
          {"Documents are not required, but strengthen the patient's appeal."}
        </p>
        <br />
        <p>{"For GLP-1 medication appeals, we highly recommend:"}</p>
        <ul>
          <li>
            <div>
              <strong>{"Letter of medical necessity"}</strong>{" "}
              {"for the patient's treatment plan"}
            </div>
          </li>
          <li>
            <div>
              <strong>{"Medical records"}</strong>{" "}
              {"that confirm the patient's diagnoses"}
            </div>
          </li>
          <li>
            <div>
              <strong>{"Relevant test results"}</strong>
              <ul className={styles.innerList}>
                <li>
                  <div>
                    <strong>{"Type 2 Diabetes:"}</strong>{" "}
                    {
                      "A1c tests, OGTT tests (2-hr glucose), FPG test, random plasma glucose"
                    }
                  </div>
                </li>
                <li>
                  <div>
                    <strong>{"Dyslipidemia/hyperlipidemia:"}</strong>{" "}
                    {"Lipid profile, lipid panel test"}
                  </div>
                </li>
                <li>
                  <div>
                    <strong>{"Sleep Apnea:"}</strong> {"Sleep Test Study"}
                  </div>
                </li>
                <li>
                  <div>
                    <strong>{"Non-alcoholic fatty liver disease:"}</strong>{" "}
                    {
                      "Blood tests (liver enzyme levels), imaging tests (ultrasounds,"
                    }
                    <br />
                    {"CT scans, MRI, FibroScan), or liver biopsy"}
                  </div>
                </li>
              </ul>
            </div>
          </li>
          <li>
            <div>
              <strong>{"Medication list"}</strong>{" "}
              {
                "that document the patient's weight management plan and lifestyle modifications"
              }
            </div>
          </li>
          <li>
            <div>
              <strong>{"Diet and exercise notes"}</strong>{" "}
              {
                "that document the patient's weight management plan and lifestyle modifications"
              }
            </div>
          </li>
        </ul>
      </>
    ),
  },
  whatDocsCanIAdd,
  whoWillSeeUploadedDocuments,
  howAreDocumentsAttachedToThePatientsAppeal,
  needMoreHelp,
];
const type2DiabetesFaqData: FrequentlyAskedQuestion[] = [
  areDocumentsRequired,
  {
    question: "What documents are recommended for GLP-1 appeals?",
    answer: (
      <>
        <p>
          {"Documents are not required, but strengthen the patient's appeal."}
        </p>
        <br />
        <p>{"For GLP-1 medication appeals, we highly recommend:"}</p>
        <ul>
          <li>
            <div>
              <strong>{"Letter of medical necessity"}</strong>{" "}
              {"for the patient's treatment plan"}
            </div>
          </li>
          <li>
            <div>
              <strong>{"Medical records"}</strong>{" "}
              {"that confirm the patient's diagnoses"}
            </div>
          </li>
          <li>
            <div>
              <strong>{"Relevant test results"}</strong>
              <ul className={styles.innerList}>
                <li>
                  <div>
                    <strong>{"Type 2 Diabetes:"}</strong>{" "}
                    {
                      "A1c tests, OGTT tests (2-hr glucose), FPG test, random plasma glucose"
                    }
                  </div>
                </li>
                <li>
                  <div>
                    <strong>{"Dyslipidemia/hyperlipidemia:"}</strong>{" "}
                    {"Lipid profile, lipid panel test"}
                  </div>
                </li>
                <li>
                  <div>
                    <strong>{"Sleep Apnea:"}</strong> {"Sleep Test Study"}
                  </div>
                </li>
                <li>
                  <div>
                    <strong>{"Non-alcoholic fatty liver disease:"}</strong>{" "}
                    {
                      "Blood tests (liver enzyme levels), imaging tests (ultrasounds,"
                    }
                    <br />
                    {"CT scans, MRI, FibroScan), or liver biopsy"}
                  </div>
                </li>
              </ul>
            </div>
          </li>
          <li>
            <div>
              <strong>{"Medication list"}</strong>{" "}
              {
                "that document the patient's diabetes management plan and lifestyle modifications"
              }
            </div>
          </li>
          <li>
            <div>
              <strong>{"Diet and exercise notes"}</strong>{" "}
              {
                "that document the patient's diabetes management plan and lifestyle modifications"
              }
            </div>
          </li>
        </ul>
      </>
    ),
  },
  whatDocsCanIAdd,
  whoWillSeeUploadedDocuments,
  howAreDocumentsAttachedToThePatientsAppeal,
  needMoreHelp,
];

const conditionToFaqDataMap: Record<string, FrequentlyAskedQuestion[]> = {
  [Condition.OBESITY]: obesityFaqData,
  [Condition.TYPE_2_DIABETES]: type2DiabetesFaqData,
  default: faqData,
};

export const FaqModal = ({
  isOpen,
  onClose,
  condition,
  initiallyOpenedQuestion,
}: FaqModalProps) => {
  const dataToUse =
    condition && Object.keys(conditionToFaqDataMap).includes(condition)
      ? conditionToFaqDataMap[condition]
      : conditionToFaqDataMap.default;
  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={<Title />}
      classNames={{ header: styles.modalHeader }}
      size="xl"
    >
      <h2 className={styles.heading}>{"Why attach documents?"}</h2>
      <Accordion
        chevron={<FontAwesomeIcon icon={faChevronDown} size="xs" />}
        defaultValue={initiallyOpenedQuestion}
      >
        {dataToUse.map((faq) => (
          <Accordion.Item key={faq.question} value={faq.question}>
            <Accordion.Control className={styles.question}>
              {faq.question}
            </Accordion.Control>
            <Accordion.Panel classNames={{ content: styles.answer }}>
              {faq.answer}
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>
    </Modal>
  );
};
